import {
  NotFoundPage,
  RequireOrganization,
} from '@startupquickstart/frontend';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireToursAccess as RequireInternalOrganization } from '@/components/routing/RequireToursAccess';
import { Layout } from './components/layout/Layout';
import { HomePage } from './pages/HomePage';
import { PersonalAccountHome } from './pages/PersonalAccountHome';

// Type pages
import {
  AmenityTypeDetailPage,
  AmenityTypeFormPage,
  AmenityTypesPage,
  AmenityTypeOverviewTab,
  AmenityTypeAmenitiesTab,
} from './pages/amenity-types';
import {
  AttractionCategoriesPage,
  AttractionCategoryDetailPage,
  AttractionCategoryFormPage,
  AttractionCategoryOverviewTab,
  AttractionCategoryAttractionTypesTab,
} from './pages/attraction-categories';
import {
  AttractionTypeDetailPage,
  AttractionTypeFormPage,
  AttractionTypesPage,
  AttractionTypeOverviewTab,
  AttractionTypeAttractionsTab,
  AttractionTypeCategoriesTab,
} from './pages/attraction-types';
import {
  LanguageDetailPage,
  LanguageFormPage,
  LanguagesPage,
  LanguageOverviewTab,
} from './pages/languages';
import {
  ThemeTypeDetailPage,
  ThemeTypeFormPage,
  ThemeTypesPage,
  ThemeTypeOverviewTab,
  ThemeTypeThemesTab,
} from './pages/theme-types';
import {
  TourTypeDetailPage,
  TourTypeFormPage,
  TourTypesPage,
  TourTypeOverviewTab,
  TourTypeToursTab,
} from './pages/tour-types';

// Location pages
import {
  GreaterAreaDetailPage,
  GreaterAreaFormPage,
  GreaterAreasPage,
  GreaterAreaOverviewTab,
  GreaterAreaNeighborhoodsTab,
  GreaterAreaHotelsTab,
  GreaterAreaToursTab,
  GreaterAreaAttractionsTab,
  GreaterAreaHealthTab,
  GreaterAreaHealthLocationTab,
  GreaterAreaHealthContentTab,
  GreaterAreaHealthTranslationsTab,
} from './pages/greater-areas';
import {
  HotelDetailPage,
  HotelFormPage,
  HotelsDashboardPage,
  HotelsPage,
  HotelOverviewTab,
  HotelToursTab,
  HotelAmenitiesTab,
  HotelAmenityFormPage,
  HotelOffersTab,
  HotelOfferFormPage,
  HotelAccessCodesTab,
  HotelAccessCodeCreatePage,
  HotelGuestsTab,
} from './pages/hotels';
import {
  NeighborhoodDetailPage,
  NeighborhoodFormPage,
  NeighborhoodsPage,
  NeighborhoodOverviewTab,
  NeighborhoodHotelsTab,
} from './pages/neighborhoods';

// Content pages
import { AmenitiesPage, AmenityDetailPage, AmenityFormPage } from './pages/amenities';
import { ProcessesPage, ProcessDetailPage, ProcessFormPage } from './pages/processes';
import { ReleaseNotesPage, ReleaseNoteDetailPage } from './pages/release-notes';
import {
  AttractionDetailPage,
  AttractionFormPage,
  AttractionsPage,
  AttractionOverviewTab,
  AttractionTypesTab,
  AttractionToursTab,
  AttractionThemesTab,
  AttractionTranslationsTab,
  AttractionGalleryTab,
  AttractionHealthTab,
} from './pages/attractions';
import {
  AttractionThemeDetailPage,
  AttractionThemeFormPage,
  AttractionThemeOverviewTab,
  AttractionThemeTranslationsTab,
} from './pages/attractions/themes';
import {
  AttractionThemeTranslationDetailPage,
  AttractionThemeTranslationFormPage,
} from './pages/attractions/themes/translations';
import {
  AttractionTranslationDetailPage,
  AttractionTranslationFormPage,
} from './pages/attractions/translations';
import {
  TourDetailPage,
  TourFormPage,
  ToursPage,
  TourOverviewTab,
  TourAttractionsTab,
  TourMapTab,
  TourHealthTab,
  TourHealthLocationTab,
  TourHealthContentTab,
  TourHealthTranslationsTab,
  TourHotelsTab,
  TourTranslationsTab,
} from './pages/tours';
import {
  TourTranslationDetailPage,
  TourTranslationFormPage,
} from './pages/tours/translations';

// Marketing pages
import {
  HotelAccessCodeDetailPage,
  HotelAccessCodeFormPage,
  HotelAccessCodesPage,
} from './pages/hotel-access-codes';
import {
  DeckShareDetailPage,
  DeckShareFormPage,
  DeckSharesPage,
} from './pages/presentation-shares';
import {
  PromotionalOfferDetailPage,
  PromotionalOfferFormPage,
  PromotionalOffersPage,
  PromotionalOfferOverviewTab,
  PromotionalOfferHotelsTab,
} from './pages/promotional-offers';
import {
  PhotoSubmissionDetailPage,
  PhotoSubmissionsPage,
  RemovalRequestDetailPage,
} from './pages/photo-submissions';
import { AccessRequestsPage } from './pages/access-requests';

// User pages
import { UserMePage } from './pages/users/UserMePage';
import {
  HotelUserDetailPage,
  HotelUserOverviewTab,
  HotelUserScansTab,
  HotelUserOffersTab,
  HotelUserActivityTab,
} from './pages/hotel-users';

// Developer Tools pages
import { ApiExplorerPage } from './pages/ApiExplorerPage';

// Import pages
import { ImportPage, ImportDetailPage } from './pages/import';

// Data Health
import { DataHealthPage } from './pages/data-health';

// Settings pages
import {
  ClerkSyncPage,
  HowToVideoPage,
  PromptDetailPage,
  PromptFormPage,
  PromptOverviewTab,
  PromptVersionHistoryTab,
  PromptsPage,
  RedisCachePage,
} from './pages/settings';

// CLI Auth
import { CliAuthPage } from './pages/CliAuthPage';

// Support
import {
  ConnectLinearPage,
  SupportTicketsPage,
  TicketDetailPage,
} from './pages/support';

export const router = createBrowserRouter([
  {
    path: '/cli-auth',
    element: <CliAuthPage />,
  },
  {
    path: '/',
    element: <Layout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      // Personal account home page (mobile app)
      {
        path: 'home',
        element: <PersonalAccountHome />,
      },
      // Non-internal organization dashboard (hotel cards)
      {
        path: 'dashboard',
        element: (
          <RequireOrganization>
            <HotelsDashboardPage />
          </RequireOrganization>
        ),
      },
      // Tours - Internal Only
      {
        path: 'tours',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ToursPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tours/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tours/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <TourOverviewTab /> },
          { path: 'attractions', element: <TourAttractionsTab /> },
          { path: 'map', element: <TourMapTab /> },
          {
            path: 'health',
            element: <TourHealthTab />,
            children: [
              { index: true, element: <Navigate to="location" replace /> },
              { path: 'location', element: <TourHealthLocationTab /> },
              { path: 'content', element: <TourHealthContentTab /> },
              { path: 'translations', element: <TourHealthTranslationsTab /> },
            ],
          },
          { path: 'hotels', element: <TourHotelsTab /> },
          { path: 'translations', element: <TourTranslationsTab /> },
          { path: 'translations/:translationId', element: <TourTranslationDetailPage /> },
        ],
      },
      {
        path: 'tours/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tours/:id/translations/:translationId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTranslationFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attractions - Internal Only
      {
        path: 'attractions',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/list/:listId',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <AttractionOverviewTab /> },
          { path: 'types', element: <AttractionTypesTab /> },
          { path: 'tours', element: <AttractionToursTab /> },
          { path: 'themes', element: <AttractionThemesTab /> },
          // Theme detail rendered inline inside the Themes tab — replaces
          // the previous standalone page so the attraction's hero/tabs
          // remain visible while drilling into a theme. The detail itself
          // has its own Overview / Translations tab strip via nested
          // children rendered into the layout's <Outlet />.
          {
            path: 'themes/:themeId',
            element: <AttractionThemeDetailPage />,
            children: [
              { index: true, element: <Navigate to="overview" replace /> },
              { path: 'overview', element: <AttractionThemeOverviewTab /> },
              {
                path: 'translations',
                element: <AttractionThemeTranslationsTab />,
              },
            ],
          },
          { path: 'translations', element: <AttractionTranslationsTab /> },
          { path: 'gallery', element: <AttractionGalleryTab /> },
          { path: 'health', element: <AttractionHealthTab /> },
        ],
      },
      {
        path: 'attractions/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Themes - Internal Only (nested under attractions)
      {
        path: 'attractions/:attractionId/themes/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:attractionId/themes/:themeId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Theme Translations - Internal Only (nested under themes)
      {
        path: 'attractions/:attractionId/themes/:themeId/translations/:translationId',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeTranslationDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:attractionId/themes/:themeId/translations/:translationId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionThemeTranslationFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Translations - Internal Only (nested under attractions)
      {
        path: 'attractions/:attractionId/translations/:translationId',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTranslationDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attractions/:attractionId/translations/:translationId/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTranslationFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Tour Types - Internal Only
      {
        path: 'tour-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tour-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'tour-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <TourTypeOverviewTab /> },
          { path: 'tours', element: <TourTypeToursTab /> },
        ],
      },
      {
        path: 'tour-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TourTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Categories - Internal Only
      {
        path: 'attraction-categories',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoriesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-categories/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoryFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-categories/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoryDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <AttractionCategoryOverviewTab /> },
          { path: 'attraction-types', element: <AttractionCategoryAttractionTypesTab /> },
        ],
      },
      {
        path: 'attraction-categories/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionCategoryFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Attraction Types - Internal Only
      {
        path: 'attraction-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'attraction-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <AttractionTypeOverviewTab /> },
          { path: 'attractions', element: <AttractionTypeAttractionsTab /> },
          { path: 'categories', element: <AttractionTypeCategoriesTab /> },
        ],
      },
      {
        path: 'attraction-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AttractionTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Amenity Types - Internal Only
      {
        path: 'amenity-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenity-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenity-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <AmenityTypeOverviewTab /> },
          { path: 'amenities', element: <AmenityTypeAmenitiesTab /> },
        ],
      },
      {
        path: 'amenity-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenityTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Theme Types - Internal Only
      {
        path: 'theme-types',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'theme-types/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'theme-types/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypeDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <ThemeTypeOverviewTab /> },
          { path: 'themes', element: <ThemeTypeThemesTab /> },
        ],
      },
      {
        path: 'theme-types/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ThemeTypeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Languages - Internal Only
      {
        path: 'languages',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguagesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'languages/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguageFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'languages/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguageDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <LanguageOverviewTab /> },
        ],
      },
      {
        path: 'languages/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <LanguageFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Greater Areas - Internal Only
      {
        path: 'greater-areas',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreasPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'greater-areas/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreaFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'greater-areas/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreaDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <GreaterAreaOverviewTab /> },
          { path: 'neighborhoods', element: <GreaterAreaNeighborhoodsTab /> },
          { path: 'hotels', element: <GreaterAreaHotelsTab /> },
          { path: 'tours', element: <GreaterAreaToursTab /> },
          { path: 'attractions', element: <GreaterAreaAttractionsTab /> },
          {
            path: 'health',
            element: <GreaterAreaHealthTab />,
            children: [
              { index: true, element: <Navigate to="location" replace /> },
              { path: 'location', element: <GreaterAreaHealthLocationTab /> },
              { path: 'content', element: <GreaterAreaHealthContentTab /> },
              { path: 'translations', element: <GreaterAreaHealthTranslationsTab /> },
            ],
          },
        ],
      },
      {
        path: 'greater-areas/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <GreaterAreaFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Neighborhoods - Internal Only
      {
        path: 'neighborhoods',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'neighborhoods/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'neighborhoods/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <NeighborhoodOverviewTab /> },
          { path: 'hotels', element: <NeighborhoodHotelsTab /> },
        ],
      },
      {
        path: 'neighborhoods/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <NeighborhoodFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Hotels - List/New/Edit are Internal Only, Detail uses wrapper
      {
        path: 'hotels',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotels/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotels/:id',
        element: (
          <RequireOrganization>
            <HotelDetailPage />
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <HotelOverviewTab /> },
          { path: 'tours', element: <HotelToursTab /> },
          { path: 'amenities', element: <HotelAmenitiesTab /> },
          { path: 'offers', element: <HotelOffersTab /> },
          { path: 'access-codes', element: <HotelAccessCodesTab /> },
          { path: 'guests', element: <HotelGuestsTab /> },
        ],
      },
      {
        path: 'hotels/:id/edit',
        element: (
          <RequireOrganization>
            <HotelFormPage />
          </RequireOrganization>
        ),
      },
      // Hotel-scoped amenity create — full-page form (sibling, not a detail tab)
      {
        path: 'hotels/:id/amenities/new',
        element: (
          <RequireOrganization>
            <HotelAmenityFormPage />
          </RequireOrganization>
        ),
      },
      // Hotel-scoped offer create/edit — full-page forms (siblings, not detail
      // tabs). Static `offers/new` resolves before dynamic `offers/:offerId`.
      {
        path: 'hotels/:id/offers/new',
        element: (
          <RequireOrganization>
            <HotelOfferFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'hotels/:id/offers/:offerId',
        element: (
          <RequireOrganization>
            <HotelOfferFormPage />
          </RequireOrganization>
        ),
      },
      // Hotel-scoped access code create — full-page form (sibling, not a detail
      // tab). Static `access-codes/new` resolves before dynamic `:accessCodeId`.
      {
        path: 'hotels/:id/access-codes/new',
        element: (
          <RequireOrganization>
            <HotelAccessCodeCreatePage />
          </RequireOrganization>
        ),
      },
      // Hotel-scoped access code detail — single page (no tabs), nested under the
      // hotel. Accessible to any organization (hotel owners + internal).
      {
        path: 'hotels/:id/access-codes/:accessCodeId',
        element: (
          <RequireOrganization>
            <HotelAccessCodeDetailPage />
          </RequireOrganization>
        ),
      },
      // Hotel User Detail - Accessible to any organization (hotel owners + internal)
      {
        path: 'hotels/:hotelId/users/:userId',
        element: (
          <RequireOrganization>
            <HotelUserDetailPage />
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <HotelUserOverviewTab /> },
          { path: 'scans', element: <HotelUserScansTab /> },
          { path: 'offers', element: <HotelUserOffersTab /> },
          { path: 'activity', element: <HotelUserActivityTab /> },
        ],
      },
      // Promotional Offers - Accessible to both (filtered for non-internal)
      {
        path: 'promotional-offers',
        element: (
          <RequireOrganization>
            <PromotionalOffersPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'promotional-offers/new',
        element: (
          <RequireOrganization>
            <PromotionalOfferFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'promotional-offers/:id',
        element: (
          <RequireOrganization>
            <PromotionalOfferDetailPage />
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <PromotionalOfferOverviewTab /> },
          { path: 'hotels', element: <PromotionalOfferHotelsTab /> },
        ],
      },
      {
        path: 'promotional-offers/:id/edit',
        element: (
          <RequireOrganization>
            <PromotionalOfferFormPage />
          </RequireOrganization>
        ),
      },
      // Hotel Access Codes - Internal Only (managed at hotel level)
      {
        path: 'hotel-access-codes',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelAccessCodesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotel-access-codes/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HotelAccessCodeFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'hotel-access-codes/:id',
        element: (
          <RequireOrganization>
            <HotelAccessCodeDetailPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'hotel-access-codes/:id/edit',
        element: (
          <RequireOrganization>
            <HotelAccessCodeFormPage />
          </RequireOrganization>
        ),
      },
      // Presentation Shares - Internal Only (sales links to the deck).
      // Every route is internal-gated, including the detail page: a share row
      // names a prospect and logs their IPs — not hotel-customer material.
      {
        path: 'presentation-shares',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <DeckSharesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'presentation-shares/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <DeckShareFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'presentation-shares/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <DeckShareDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'presentation-shares/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <DeckShareFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Amenities - Internal Only (managed at hotel level)
      {
        path: 'amenities',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AmenitiesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'amenities/new',
        element: (
          <RequireOrganization>
            <AmenityFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'amenities/:id',
        element: (
          <RequireOrganization>
            <AmenityDetailPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'amenities/:id/edit',
        element: (
          <RequireOrganization>
            <AmenityFormPage />
          </RequireOrganization>
        ),
      },
      {
        path: 'users/me',
        element: (
          <RequireOrganization>
            <UserMePage />
          </RequireOrganization>
        ),
      },
      // Photo Submissions - Internal Only (review workflow)
      {
        path: 'photo-submissions',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <PhotoSubmissionsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'photo-submissions/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <PhotoSubmissionDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'photo-submissions/removal-requests/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <RemovalRequestDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Access Requests - Internal Only (hotel-portal review workflow)
      {
        path: 'access-requests',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <AccessRequestsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Developer Tools - Internal Only
      {
        path: 'api-explorer',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ApiExplorerPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Import - Internal Only
      {
        path: 'import',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ImportPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'import/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ImportDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Data Health - Internal Only
      {
        path: 'data-health',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <DataHealthPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Settings - Internal Only
      {
        path: 'settings/redis-cache',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <RedisCachePage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'settings/clerk-sync',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ClerkSyncPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'settings/how-to-video',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <HowToVideoPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Processes - Internal Only
      {
        path: 'processes',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ProcessesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'processes/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ProcessFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'processes/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ProcessDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'processes/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ProcessFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Release Notes - Internal Only (read-only; content synced from repo)
      {
        path: 'release-notes',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ReleaseNotesPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'release-notes/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ReleaseNoteDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Prompts - Internal Only
      {
        path: 'settings/prompts',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <PromptsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'settings/prompts/new',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <PromptFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'settings/prompts/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <PromptDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
        children: [
          { path: 'overview', element: <PromptOverviewTab /> },
          { path: 'versions', element: <PromptVersionHistoryTab /> },
        ],
      },
      {
        path: 'settings/prompts/:id/edit',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <PromptFormPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // Support — Internal Only (path-prefixed with /internal to make scope explicit)
      {
        path: 'internal/support',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <SupportTicketsPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'internal/support/connect',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <ConnectLinearPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      {
        path: 'internal/support/:id',
        element: (
          <RequireOrganization>
            <RequireInternalOrganization>
              <TicketDetailPage />
            </RequireInternalOrganization>
          </RequireOrganization>
        ),
      },
      // fallback
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
]);
